import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { MessageBar as SparkyMessageBar } from '@eneco/sparky';
import type { MessageBarProps } from '@eneco/sparky/types';

import type { MessageBarContent } from './message-bar-content.types';

/**
 * `MessageBar` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/MessageBar/MessageBar.tsx`): the
 * bar wrapped in a plain anchor when it resolves a link — Sparky's own `MessageBar` also accepts an
 * `href` directly, but the legacy component never passes one, rendering the inner bar in its
 * "standalone" style regardless; kept identical here rather than "fixed".
 */
const MessageBar: FC = () => {
  const { adapter } = useCms();
  const content = useContent<MessageBarContent>();
  const headingLevel = useSetting('headingLevel') as MessageBarProps['headingLevel'];

  const html = adapter.toRichText(content.content);
  const link = adapter.toLink(content.link);

  const bar = <SparkyMessageBar headingLevel={headingLevel} text={html ? <RichTextComponent html={html} /> : undefined} title={content.title} />;

  return link?.href ? (
    <a href={link.href} target={link.target} rel={link.target === '_blank' ? 'noreferrer' : undefined}>
      {bar}
    </a>
  ) : (
    bar
  );
};

export default MessageBar;
